/**
 * 스레드 글의 덧붙는 조각 — 사진 · 고른 작품 · 모금 집행 결과 · 하트와 댓글 숫자.
 * @description 앱 `CollabThreadItemBody` 의 조각들을 웹에 맞게 줄였다. 모두 보기 전용이다 — 하트와
 *   댓글은 숫자만 보이고, 남기는 것은 앱에서 한다.
 */
import { Heart, MessageCircle } from "lucide-react";
import { memo } from "react";

import { collabV3ThreadStyles as styles } from "./CollabV3Thread.styles";
import {
  COLLAB_V3_THREAD_IMAGE_SIZES,
  COLLAB_V3_THREAD_MAX_IMAGES,
} from "./CollabV3Thread.styles";
import type {
  CollabV3ThreadExecutionProps,
  CollabV3ThreadMediaProps,
  CollabV3ThreadPickedProps,
  CollabV3ThreadReactionsProps,
} from "./CollabV3Thread.types";

import { COLLAB_V3_COPY } from "../../constants/collabV3Copy";
import { COLLAB_V3_CARD_COPY } from "../../constants/collabV3CardCopy";
import { formatCount } from "../../utils/collabV3Format";
import { AppImage, IMAGE_PLACEHOLDER } from "@/shared/ui";

const copy = COLLAB_V3_COPY.thread;
const ls = (amount: number) => COLLAB_V3_CARD_COPY.ls(formatCount(amount));

/** 사진 — 3열, 웹에서는 앞 6장까지 */
export const CollabV3ThreadMedia = memo<CollabV3ThreadMediaProps>(
  ({ imageUrls }) => (
    <ul className={styles.media}>
      {imageUrls.slice(0, COLLAB_V3_THREAD_MAX_IMAGES).map((url) => (
        <li key={url} className={styles.mediaItem}>
          <AppImage uri={url} sizes={COLLAB_V3_THREAD_IMAGE_SIZES} />
        </li>
      ))}
    </ul>
  ),
);
CollabV3ThreadMedia.displayName = "CollabV3ThreadMedia";

/** 마무리 글이 고른 작품 — 그림 · 낸 사람 · 주최자의 한 줄 */
export const CollabV3ThreadPicked = memo<CollabV3ThreadPickedProps>(
  ({ works }) => (
    <ul className={styles.picked}>
      {works.map((work) => (
        <li key={work.workId} className={styles.pickedItem}>
          <span className={styles.mediaItem}>
            <AppImage
              uri={work.thumbnailUrl ?? ""}
              fallback={IMAGE_PLACEHOLDER}
              altText={work.authorName}
              sizes={COLLAB_V3_THREAD_IMAGE_SIZES}
            />
          </span>
          <span className={styles.pickedName}>{work.authorName}</span>
          {work.message ? (
            <span className={styles.pickedName}>{work.message}</span>
          ) : null}
        </li>
      ))}
    </ul>
  ),
);
CollabV3ThreadPicked.displayName = "CollabV3ThreadPicked";

/** 모금 집행 결과 — 어디에 얼마를 썼는지, 남은 LS 를 어디에 썼는지 */
export const CollabV3ThreadExecution = memo<CollabV3ThreadExecutionProps>(
  ({ execution }) => (
    <div className={styles.box}>
      {execution.usages.map((usage) => (
        <p key={usage.title} className={styles.usage}>
          <span>{usage.title}</span>
          <span>{ls(usage.amount)}</span>
        </p>
      ))}
      <p className={`${styles.usage} ${styles.usageTotal}`}>
        <span>{copy.execution}</span>
        <span>{ls(execution.usedAmount)}</span>
      </p>
      {execution.leftoverNote ? (
        <p className={styles.usage}>
          <span>{copy.leftover}</span>
          <span>{execution.leftoverNote}</span>
        </p>
      ) : null}
    </div>
  ),
);
CollabV3ThreadExecution.displayName = "CollabV3ThreadExecution";

/** 하트 · 댓글 숫자 — 누르지 않는다 */
export const CollabV3ThreadReactions = memo<CollabV3ThreadReactionsProps>(
  ({ likeCount, commentCount }) => (
    <p className={styles.reactions}>
      <span className={styles.reaction}>
        <Heart aria-hidden className={styles.reactionIcon} />
        <span className="sr-only">{COLLAB_V3_COPY.a11y.like}</span>
        {formatCount(likeCount)}
      </span>
      <span className={styles.reaction}>
        <MessageCircle aria-hidden className={styles.reactionIcon} />
        <span className="sr-only">{copy.replies(commentCount)}</span>
        <span aria-hidden>{formatCount(commentCount)}</span>
      </span>
    </p>
  ),
);
CollabV3ThreadReactions.displayName = "CollabV3ThreadReactions";
